/**
 * Header parameters (contracts/ui-api.md "Header parameters (sheet 40)"; GEN.3,
 * GEN.6, TEN.4, D.3; DM-01M3BGTA0D7JMNSAMXC12E16MT: full edit, replace-only,
 * no Reveal). Identifiers come back masked and are rendered as given
 * (DM-01M3C8MKBH360E5HRVH5KXF9TP).
 */
import { api } from '../api'

export type Bureau = 'equifax' | 'experian' | 'transunion' | 'innovis'
export const BUREAUS: readonly Bureau[] = ['equifax', 'experian', 'transunion', 'innovis']

export interface ProgramIdentifier {
  set: boolean
  masked: string | null
}

/** GET and PUT /v1/header-params answer this shape. */
export interface HeaderParams {
  reporter_name: string
  reporter_address: string
  reporter_telephone: string
  program_identifiers: Record<Bureau, ProgramIdentifier>
  updated_at: string | null
}

/**
 * The PUT body. The three reporter fields are always sent (an omitted one is
 * cleared); `program_identifiers` carries only the bureaus replaced (a string)
 * or cleared (`null`), and an absent bureau keeps its stored value.
 */
export interface HeaderParamsBody {
  reporter_name: string
  reporter_address: string
  reporter_telephone: string
  program_identifiers?: Partial<Record<Bureau, string | null>>
}

export interface HeaderParamsEdit {
  reporter_name: string
  reporter_address: string
  reporter_telephone: string
  program_identifiers: Partial<Record<Bureau, string | null>>
}

/** The PUT body for an edit in which `changed` bureaus were replaced or cleared (FR-022). */
export function headerParamsBody(edited: HeaderParamsEdit, changed: readonly Bureau[]): HeaderParamsBody {
  const body: HeaderParamsBody = {
    reporter_name: edited.reporter_name,
    reporter_address: edited.reporter_address,
    reporter_telephone: edited.reporter_telephone,
  }
  const ids: Partial<Record<Bureau, string | null>> = {}
  for (const bureau of BUREAUS) {
    if (changed.includes(bureau)) ids[bureau] = edited.program_identifiers[bureau] ?? null
  }
  if (Object.keys(ids).length > 0) body.program_identifiers = ids
  return body
}

export async function getHeaderParams(): Promise<HeaderParams> {
  return api<HeaderParams>('/header-params')
}

/** PUT /v1/header-params, built key by key so nothing undocumented is sent (C-006). */
export async function putHeaderParams(body: HeaderParamsBody): Promise<HeaderParams> {
  const clean: HeaderParamsBody = {
    reporter_name: body.reporter_name,
    reporter_address: body.reporter_address,
    reporter_telephone: body.reporter_telephone,
  }
  const given = body.program_identifiers
  if (given !== undefined) {
    const ids: Partial<Record<Bureau, string | null>> = {}
    for (const bureau of BUREAUS) if (bureau in given) ids[bureau] = given[bureau] ?? null
    clean.program_identifiers = ids
  }
  return api<HeaderParams>('/header-params', { method: 'PUT', body: JSON.stringify(clean) })
}
